/**
 * WorkspaceSwitcher — dropdown below the sidebar logo that lets the user
 * change the active workspace. Driven entirely by `useWorkspaceContext()`
 * so all other pages react to the change via existing hooks.
 *
 * Behaviour matches the Kora pattern + the SocialSuite brief §7.1:
 *   - Click opens a dropdown listing every workspace with a role badge
 *   - Active workspace has an orange filled-dot indicator
 *   - Click-away + Escape close
 *   - Arrow keys navigate, Enter selects
 *   - "Create new workspace" at the bottom is a no-op (Coming soon toast)
 *     because the real create flow is a separate brief
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { useWorkspaceContext } from '@/components/WorkspaceProvider';
import { billingApi } from '@/lib/api';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';

interface Props {
  collapsed?: boolean;
}

export function WorkspaceSwitcher({ collapsed = false }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeWorkspace, workspaces, setActiveWorkspaceId } = useWorkspaceContext();
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // One-shot post-login pulse. AuthCallbackPage + LoginPage both stash
  // `mf_just_logged_in=1` in sessionStorage on success. We read+clear
  // it on mount and let a CSS animation run for ~6s. Only fires when
  // the user actually has more than one workspace — pulsing for a
  // user with a single workspace would be noise. Trial-expired users
  // who belong to a paid workspace are the real audience.
  const [postLoginPulse, setPostLoginPulse] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem('mf_just_logged_in') !== '1') return;
    sessionStorage.removeItem('mf_just_logged_in');
    if (workspaces.length > 1) {
      setPostLoginPulse(true);
      const timer = setTimeout(() => setPostLoginPulse(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [workspaces.length]);
  // Clear pulse early if the user clicks anywhere on the switcher.
  useEffect(() => {
    if (postLoginPulse && open) setPostLoginPulse(false);
  }, [postLoginPulse, open]);

  // Plan check for the "Create new workspace" entry. Hide when the
  // active workspace is on a plan that can't add another (trial
  // capped at 1, paused, or already at the tier max). Without this
  // the user would click + see a 402 from /api/workspaces with no
  // path forward.
  const { data: billingStatus } = useQuery({
    queryKey: ['billing-status', activeWorkspace?.id],
    queryFn: () => billingApi.status(activeWorkspace!.id),
    enabled: !!activeWorkspace?.id,
    staleTime: 60_000,
  });
  const ownedCount = workspaces.filter((w) => w.role === 'OWNER').length;
  const canCreateWorkspace = (() => {
    const plan = billingStatus?.plan;
    if (!plan) return true; // unknown → optimistic
    if (plan === 'paused') return false;
    if (plan === 'agency') return true; // unlimited
    if (plan === 'trial' || plan === 'starter') return ownedCount < 1;
    if (plan === 'pro' || plan === 'beta') return ownedCount < 5;
    return true;
  })();

  // Reset focus ring when opening
  useEffect(() => {
    if (!open) return;
    const activeIdx = workspaces.findIndex((w) => w.id === activeWorkspace?.id);
    setFocusIndex(activeIdx >= 0 ? activeIdx : 0);
  }, [open, workspaces, activeWorkspace?.id]);

  // Click-away + Escape close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (id: string) => {
    if (id !== activeWorkspace?.id) {
      setActiveWorkspaceId(id);
      // Navigate to the dashboard root so page-level queries refetch cleanly
      // under the new workspace scope. Without this the current page stays
      // but its cached data is for the previous workspace.
      navigate('/dashboard');
    }
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, workspaces.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = workspaces[focusIndex];
      if (target) handleSelect(target.id);
    }
  };

  if (!activeWorkspace && workspaces.length === 0) {
    // Not loaded yet — render a neutral placeholder that matches the collapsed
    // pill height so the nav below doesn't jump on hydrate.
    return <div className="mx-4 mt-3 h-9" />;
  }

  // Collapsed mode: compact pill showing the first letter only, no dropdown.
  if (collapsed) {
    return (
      <div className="mx-2 mt-3 mb-2 flex justify-center">
        <div
          className="bg-subtle text-text-primary flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold"
          title={activeWorkspace?.name ?? t('workspace.title')}
        >
          {(activeWorkspace?.name?.[0] ?? 'W').toUpperCase()}
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-4 mt-3 mb-2" ref={wrapRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-text-primary hover:border-accent/40 dark:hover:border-accent/50 flex w-full items-center justify-between gap-2 rounded-xl border border-[#e5e2db] bg-[#faf9f7] px-3 py-2 text-sm font-semibold transition-colors hover:bg-[#f3f1ed] dark:border-white/[0.08] dark:bg-white/[0.04] dark:hover:bg-white/[0.06] ${
          open ? 'ring-accent/40 ring-1' : ''
        } ${postLoginPulse ? 'mk-switcher-pulse' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('workspace.switchWorkspace')}
      >
        <span className="truncate" data-testid="workspace-name">
          {activeWorkspace?.name ?? t('workspace.selectWorkspace')}
        </span>
        <ChevronDown
          className={`text-text-tertiary h-4 w-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul
          className="absolute top-full right-0 left-0 z-50 mt-2 overflow-hidden rounded-xl border border-[#e5e2db] bg-[#faf9f7] p-1 dark:border-white/[0.08] dark:bg-[#13131c]"
          role="listbox"
        >
          {workspaces.map((w, i) => {
            const isActive = w.id === activeWorkspace?.id;
            const isFocused = i === focusIndex;
            return (
              <li key={w.id} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => handleSelect(w.id)}
                  onMouseEnter={() => setFocusIndex(i)}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    isFocused ? 'bg-accent/[0.08]' : 'hover:bg-subtle'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={`flex h-2 w-2 flex-shrink-0 items-center justify-center rounded-full ${
                        isActive
                          ? 'bg-accent shadow-[0_0_6px_rgba(232,84,10,0.6)]'
                          : 'bg-transparent'
                      }`}
                      aria-hidden
                    />
                    <span
                      className={`truncate ${
                        isActive ? 'text-accent font-semibold' : 'text-text-primary'
                      }`}
                    >
                      {w.name}
                    </span>
                    {isActive && <Check className="text-accent h-3.5 w-3.5 flex-shrink-0" />}
                  </span>
                  <RoleBadge role={w.role} />
                </button>
              </li>
            );
          })}

          {canCreateWorkspace && (
            <>
              {/* Divider */}
              <li role="separator">
                <div className="my-1 border-t border-[#e5e2db] dark:border-white/[0.08]" />
              </li>
              {/* Create new workspace — opens the real CreateWorkspaceModal.
                  Hidden entirely when the user's active plan is paused or
                  already at its workspace cap (no point showing a button
                  that would 402 on click). */}
              <li role="option" aria-selected={false}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setCreateOpen(true);
                  }}
                  className="text-text-secondary hover:bg-subtle hover:text-accent flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('workspace.createNew')}
                </button>
              </li>
            </>
          )}
        </ul>
      )}

      {createOpen && <CreateWorkspaceModal onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RoleBadge — small rounded pill beside the workspace name
// ---------------------------------------------------------------------------

function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation();
  const label = t(`workspace.role_${role.toUpperCase()}`, {
    defaultValue: role.toLowerCase(),
  });
  const tone =
    role === 'OWNER'
      ? 'bg-accent/10 text-accent'
      : role === 'ADMIN'
        ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
        : 'bg-text-tertiary/15 text-text-tertiary';
  return (
    <span
      className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase ${tone}`}
    >
      {label}
    </span>
  );
}
